// src/store/SystemJobsSlice.js
import {
  createSlice,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { axiosInstance } from "services/axios-supabase";
import { supabase } from "services/supabase";

// ------------------------------------------------------------------------
// Initial state
const initialState = {
  jobs: [],
  loading: false,
  error: null,
  triggering: false,
  triggerError: null,
  triggerSuccess: null,
  jobsLoaded: false,
  streaming: false,
  streamChanges: [],
  streamError: null,
};

// ------------------------------------------------------------------------
// Async Thunks

// Fetch list of available system jobs
export const fetchSystemJobs = createAsyncThunk(
  "systemJobs/fetchSystemJobs",
  async (_, { rejectWithValue }) => {
    try {
      // Ensure we have a valid session before making the request
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        return rejectWithValue("Authentication required. Please log in.");
      }

      const response = await axiosInstance.get("/system/jobs", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      
      // Handle different response structures
      let jobsData = response.data?.jobs || response.data?.data || response.data || [];
      
      // If jobsData is an object (key-value pairs), convert it to an array
      if (!Array.isArray(jobsData) && typeof jobsData === 'object' && jobsData !== null) {
        jobsData = Object.entries(jobsData).map(([key, value]) => {
          // If value is a string, treat it as description
          if (typeof value === 'string') {
            return {
              id: key,
              name: key,
              description: value,
            };
          }
          // If value is an object, merge it with the key
          if (typeof value === 'object' && value !== null) {
            return {
              id: key,
              name: key,
              ...value,
            };
          }
          // Fallback
          return {
            id: key,
            name: key,
            value: value,
          };
        });
      }
      
      return Array.isArray(jobsData) ? jobsData : [];
    } catch (error) {
      console.error("Error fetching system jobs:", error);
      return rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to fetch system jobs"
      );
    }
  }
);

// Trigger a system job
export const triggerSystemJob = createAsyncThunk(
  "systemJobs/triggerSystemJob",
  async ({ eventName, data }, { rejectWithValue }) => {
    try {
      // Ensure we have a valid session before making the request
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        return rejectWithValue("Authentication required. Please log in.");
      }

      // Parse JSON data if it's a string
      let parsedData = {};
      if (data) {
        try {
          parsedData = typeof data === "string" ? JSON.parse(data) : data;
        } catch (parseError) {
          return rejectWithValue("Invalid JSON data format");
        }
      }

      const response = await axiosInstance.post(
        "/system/jobs",
        {
          event: eventName,
          data: parsedData,
        },
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error triggering system job:", error);
      return rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to trigger system job"
      );
    }
  }
);

// ------------------------------------------------------------------------
// Selectors
export const selectSystemJobs = (state) => state.systemJobsReducer.jobs;
export const selectSystemJobsLoading = (state) => state.systemJobsReducer.loading;
export const selectSystemJobsError = (state) => state.systemJobsReducer.error;
export const selectSystemJobsLoaded = (state) => state.systemJobsReducer.jobsLoaded;
export const selectTriggeringJob = (state) => state.systemJobsReducer.triggering;
export const selectTriggerJobError = (state) => state.systemJobsReducer.triggerError;
export const selectTriggerJobSuccess = (state) => state.systemJobsReducer.triggerSuccess;
export const selectStreaming = (state) => state.systemJobsReducer.streaming;
export const selectStreamChanges = (state) => state.systemJobsReducer.streamChanges;
export const selectStreamError = (state) => state.systemJobsReducer.streamError;

// ------------------------------------------------------------------------
// Slice
export const SystemJobsSlice = createSlice({
  name: "systemJobs",
  initialState,
  reducers: {
    clearTriggerError: (state) => {
      state.triggerError = null;
    },
    clearTriggerSuccess: (state) => {
      state.triggerSuccess = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setStreaming: (state, action) => {
      state.streaming = action.payload;
    },
    addStreamChange: (state, action) => {
      state.streamChanges.push({
        id: Date.now() + Math.random(),
        timestamp: new Date().toISOString(),
        data: action.payload,
      });
    },
    clearStreamChanges: (state) => {
      state.streamChanges = [];
    },
    setStreamError: (state, action) => {
      state.streamError = action.payload;
    },
    clearStreamError: (state) => {
      state.streamError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ------------------------------------------------------------
      // Fetch System Jobs
      .addCase(fetchSystemJobs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSystemJobs.fulfilled, (state, action) => {
        state.loading = false;
        state.jobs = action.payload;
        state.jobsLoaded = true;
        state.error = null;
      })
      .addCase(fetchSystemJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ------------------------------------------------------------
      // Trigger System Job
      .addCase(triggerSystemJob.pending, (state) => {
        state.triggering = true;
        state.triggerError = null;
        state.triggerSuccess = null;
      })
      .addCase(triggerSystemJob.fulfilled, (state, action) => {
        state.triggering = false;
        state.triggerSuccess = action.payload;
        state.triggerError = null;
      })
      .addCase(triggerSystemJob.rejected, (state, action) => {
        state.triggering = false;
        state.triggerError = action.payload;
        state.triggerSuccess = null;
      });
  },
});

// Export actions
export const { 
  clearTriggerError, 
  clearTriggerSuccess, 
  clearError,
  setStreaming,
  addStreamChange,
  clearStreamChanges,
  setStreamError,
  clearStreamError,
} = SystemJobsSlice.actions;

// ------------------------------------------------------------------------
// Streaming Helper Function
// This function can be called from components to start streaming
// It uses axiosInstance to get base URL and auth token
let activeStreamController = null;

export const startJobStream = (streamUrl) => {
  return async (dispatch, getState) => {
    try {
      // Stop any existing stream
      if (activeStreamController) {
        activeStreamController.active = false;
        activeStreamController.reader.cancel();
        activeStreamController = null;
      }

      dispatch(clearStreamChanges());
      dispatch(clearStreamError());
      dispatch(setStreaming(true));

      // Get session for auth token
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        dispatch(setStreamError("Authentication required. Please log in."));
        dispatch(setStreaming(false));
        return;
      }

      // Use fetch for streaming with the full streamUrl as-is
      const response = await fetch(streamUrl, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Accept': 'text/event-stream',
        },
      });

      if (!response.ok) {
        throw new Error(`Stream failed: ${response.status} ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No stream reader available');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      const streamController = {
        reader,
        active: true,
      };

      activeStreamController = streamController;

      const processStream = async () => {
        try {
          while (streamController.active) {
            const { done, value } = await reader.read();
            
            if (done) {
              // Add completion message to stream changes
              dispatch(addStreamChange({ 
                message: "Stream completed successfully" 
              }));
              dispatch(setStreaming(false));
              activeStreamController = null;
              break;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.trim() && streamController.active) {
                try {
                  const dataMatch = line.match(/^data:\s*(.+)$/m);
                  if (dataMatch) {
                    const data = JSON.parse(dataMatch[1]);
                    dispatch(addStreamChange(data));
                  } else if (line.trim().startsWith('{')) {
                    const data = JSON.parse(line.trim());
                    dispatch(addStreamChange(data));
                  }
                } catch (error) {
                  console.error("Error parsing stream message:", error);
                  dispatch(addStreamChange({ raw: line, parseError: error.message }));
                }
              }
            }
          }
        } catch (error) {
          console.error("Stream reading error:", error);
          if (streamController.active) {
            // Add error message to stream changes
            dispatch(addStreamChange({ 
              message: `Stream error: ${error.message}` 
            }));
            dispatch(setStreamError(error.message));
            dispatch(setStreaming(false));
            activeStreamController = null;
          }
        }
      };

      processStream();
    } catch (error) {
      console.error("Error starting stream:", error);
      dispatch(setStreamError(error.response?.data?.message || error.message));
      dispatch(setStreaming(false));
      activeStreamController = null;
    }
  };
};

export const stopJobStream = () => {
  return (dispatch) => {
    if (activeStreamController) {
      // Add stopped message to stream changes
      dispatch(addStreamChange({ 
        message: "Stream stopped by user" 
      }));
      activeStreamController.active = false;
      activeStreamController.reader.cancel();
      activeStreamController = null;
    }
    dispatch(setStreaming(false));
  };
};

// Export reducer
export default SystemJobsSlice.reducer;

